import type {Price, DiscountType} from '@/redux/v1/models';
import type {ExtractPropsWithoutRef} from '@/types';

import styled from '@emotion/styled';
import {LabelSmallBold} from '@travel-frontend/ui-components';
import {Trans, useTranslation} from 'react-i18next';

import {HelpTooltip} from '@/components/HelpTooltip';
import {Container} from '@/components/PriceTag/components/Container';
import {DiscountedTotalPriceOriginal} from '@/components/PriceTag/components/DiscountedTotalPriceOriginal';
import {PricePerPerson} from '@/components/PriceTag/components/PricePerPerson';
import {TotalPrice} from '@/components/PriceTag/components/TotalPrice';
import {priceFormat} from '@/utils/numberFormat';
import {testId} from '@/utils/testId';

type Props = ExtractPropsWithoutRef<typeof Container> & {
  totalPrice: Price;
  totalPriceInternal?: Price | null;
  totalPriceOriginal?: Price | null;
  totalDiscountHint?: string | null;
  totalDiscount: Price | null;
  pricePerPerson?: Price | null;
  isExact?: boolean;
  hasCompactNotation?: boolean; // @FIXME maybe not the best name?
  signDisplay?: Intl.NumberFormatOptions['signDisplay'];
  size?: 'small' | 'medium' | 'large';
  isBold?: boolean;
  discountType: DiscountType;
  discountPercentage: number;
  totalDiscountOriginal?: Price;
  canShowPriceCache?: boolean;
};

export const PriceTag = styled(
  ({
    totalPrice,
    totalPriceInternal = null,
    totalPriceOriginal = null,
    totalDiscountHint = null,
    pricePerPerson = null,
    isExact = false,
    hasCompactNotation = false,
    canShowPriceCache = false,
    totalDiscount,
    signDisplay,
    size = 'medium',
    isBold = true,
    discountType,
    discountPercentage,
    totalDiscountOriginal,
    ...restProps
  }: Props) => {
    const {t} = useTranslation();
    const fractionDigits = hasCompactNotation ? 0 : undefined;

    const discountAmount = totalDiscount?.amount ?? totalDiscount;
    const isTotalPriceValid =
      totalPriceOriginal !== null &&
      totalPriceOriginal.amount > totalPrice.amount;
    const isDiscountAmountValid =
      (discountAmount !== null &&
        typeof discountAmount === 'number' &&
        discountAmount > 0) ||
      (discountType === 'ISEARCH_DISCOUNT' && discountPercentage > 0);

    return (
      <Container {...restProps}>
        {isTotalPriceValid && isDiscountAmountValid && (
          <DiscountedTotalPriceOriginal
            canShowPriceCache={canShowPriceCache}
            totalPriceOriginal={totalPriceOriginal}
            totalDiscountHint={totalDiscountHint}
            totalDiscount={totalDiscount}
            totalPrice={totalPrice}
            fractionDigits={fractionDigits}
            discountType={discountType}
            discountPercentage={discountPercentage}
            totalDiscountOriginal={totalDiscountOriginal}
            size={size}
          />
        )}
        <TotalPrice
          isBold={isBold}
          size={size}
          data-testid={testId('total-price')}
        >
          {isExact ? (
            <b>
              {priceFormat(totalPrice, {
                fractionDigits,
                signDisplay,
              })}
            </b>
          ) : (
            <Trans
              i18nKey="general:priceFrom"
              values={{
                price: totalPrice.amount,
              }}
              components={[<b key="boldTotalPrice" />]}
              tOptions={{
                formatParams: {
                  price: {
                    fractionDigits,
                    signDisplay,
                    currency: totalPrice.currency,
                  },
                },
              }}
            />
          )}
          {totalPrice.currency === 'CHF' && totalPriceInternal !== null && (
            <HelpTooltip
              content={
                <>
                  <LabelSmallBold>
                    {t('priceNotice.CHF.title', {
                      price: totalPriceInternal.amount,
                      formatParams: {
                        // @FIXME dynamic
                        price: {
                          locale: 'de-DE',
                          currency: totalPriceInternal.currency,
                        },
                      },
                    })}
                  </LabelSmallBold>
                  <p>{t('priceNotice.CHF.content')}</p>
                </>
              }
              data-interaction-trigger
            />
          )}
        </TotalPrice>
        {pricePerPerson !== null &&
          pricePerPerson.amount !== totalPrice.amount && (
            <PricePerPerson data-testid={testId('price-perPerson')}>
              {t('pricePerPersonShort', {
                price: pricePerPerson.amount,
                currency: pricePerPerson.currency,
                formatParams: {price: {fractionDigits}},
              })}
            </PricePerPerson>
          )}
      </Container>
    );
  }
)``;
